import React, {Fragment} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {Autocomplete, CircularProgress, TextField, Typography} from '@mui/material';

import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {ProfessionalsCouncils} from 'domain/usecases';
import {RequestErrorModal} from 'presentation/pages/private/refund/components';
import {useGetCouncilTypes} from '../../../../hooks/useGetCouncilTypes';
import {CouncilTypeSelectProps} from './types';

const {councilTypePlaceholder} =
	translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;
const {loading: loadingText, notFound: notFoundText} = translations['pt-br'].privateFlow.refund;
const {requiredField} = translations['pt-br'].commons;

export const CouncilTypeSelect = ({name}: CouncilTypeSelectProps) => {
	const {control, clearErrors} = useFormContext();

	const {loadingList, options, open, handleClose, handleOpen, fetchError, setFetchError} =
		useGetCouncilTypes();

	function formatCouncilOptionText(option: ProfessionalsCouncils) {
		if (!option) {
			return '';
		}
		if (option.acronym === 'OUT') {
			return formatText(option.description);
		}
		return `${option.acronym} - ${formatText(option.description)}`;
	}

	return (
		<Fragment>
			<Controller
				control={control}
				name={name}
				render={({field: {value, onChange}, fieldState: {invalid}}) => (
					<Autocomplete
						open={open}
						onOpen={handleOpen}
						onClose={handleClose}
						onChange={(_event, selectedOptions) => {
							onChange(selectedOptions);
							clearErrors(name);
						}}
						value={value?.acronym ? value : null}
						isOptionEqualToValue={(option, valueOption) =>
							option.acronym === valueOption.acronym
						}
						getOptionLabel={option => formatCouncilOptionText(option)}
						options={options}
						loading={loadingList}
						loadingText={loadingText}
						noOptionsText={notFoundText}
						renderInput={params => (
							<TextField
								{...params}
								error={invalid}
								placeholder={councilTypePlaceholder}
								label={councilTypePlaceholder}
								slotProps={{
									input: {
										...params.InputProps,
										endAdornment: (
											<React.Fragment>
												{loadingList ? (
													<CircularProgress color="inherit" size={20} />
												) : null}
												{params?.InputProps?.endAdornment}
											</React.Fragment>
										),
									},
								}}
								helperText={
									invalid ? (
										<Typography
											component="span"
											variant="helperText"
											color="error">
											{requiredField}
										</Typography>
									) : null
								}
							/>
						)}
					/>
				)}
			/>

			<RequestErrorModal open={fetchError} setOpen={() => setFetchError(false)} />
		</Fragment>
	);
};
